import {
  FALLBACK_HOTLINES,
  buildMarketplacePath,
  buildResponderPath,
  normalizeHotline,
  uniqueResponders,
  type MarketplaceHotline,
  type MarketplaceResponder
} from "./marketplace-data";

type MarketplaceIndexableDetailInput =
  | {
      type: "responder";
      responderId: string;
      title?: string;
      description?: string;
    }
  | {
      type: "hotline";
      hotlineId: string;
      title?: string;
      description?: string;
    };

export type MarketplaceIndexableDetail = {
  path: string;
  title: string;
  description: string;
  breadcrumbs: Array<{ name: string; path: string }>;
};

// 默认不开放 responder / hotline 详情页收录；只有明确加入白名单的稳定公开页才会进入 sitemap
// 和 nginx 例外配置。
const MARKETPLACE_INDEXABLE_DETAILS: MarketplaceIndexableDetailInput[] = [];

const normalizedHotlines = FALLBACK_HOTLINES.map(normalizeHotline);
const responderMap = new Map<string, MarketplaceResponder>(uniqueResponders(normalizedHotlines).map((item) => [item.responder_id, item]));
const hotlineMap = new Map<string, MarketplaceHotline>(normalizedHotlines.map((item) => [item.hotline_id, item]));

function buildResponderSeoEntry(input: Extract<MarketplaceIndexableDetailInput, { type: "responder" }>) {
  const responder = responderMap.get(input.responderId);
  if (!responder) {
    return null;
  }

  const path = buildResponderPath(responder);

  return {
    path,
    title: input.title ?? `${responder.display_name || responder.responder_id} — CALL ANYTHING Marketplace`,
    description:
      input.description ??
      `${responder.display_name || responder.responder_id} 在 CALL ANYTHING Marketplace 上公开发布的 Hotline 目录与能力概览。`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Marketplace", path: "/marketplace/" },
      { name: responder.display_name || responder.responder_id, path }
    ]
  } satisfies MarketplaceIndexableDetail;
}

function buildHotlineSeoEntry(input: Extract<MarketplaceIndexableDetailInput, { type: "hotline" }>) {
  const hotline = hotlineMap.get(input.hotlineId);
  if (!hotline) {
    return null;
  }

  const responderPath = buildResponderPath(hotline);
  const path = buildMarketplacePath(hotline);

  return {
    path,
    title: input.title ?? `${hotline.display_name} — CALL ANYTHING Marketplace`,
    description:
      input.description ??
      hotline.summary ??
      hotline.description ??
      `${hotline.display_name} 在 CALL ANYTHING Marketplace 上的公开 Hotline 详情页。`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Marketplace", path: "/marketplace/" },
      { name: hotline.responder_id, path: responderPath },
      { name: hotline.display_name, path }
    ]
  } satisfies MarketplaceIndexableDetail;
}

export const INDEXABLE_MARKETPLACE_DETAIL_ROUTES = MARKETPLACE_INDEXABLE_DETAILS.map((entry) =>
  entry.type === "responder" ? buildResponderSeoEntry(entry) : buildHotlineSeoEntry(entry)
).filter((entry): entry is MarketplaceIndexableDetail => Boolean(entry));

export function getIndexableMarketplaceDetail(pathname: string) {
  const normalizedPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "") || "/";
  return INDEXABLE_MARKETPLACE_DETAIL_ROUTES.find((entry) => entry.path === normalizedPath);
}

export function buildMarketplaceDetailExceptionsNginx() {
  if (INDEXABLE_MARKETPLACE_DETAIL_ROUTES.length === 0) {
    return "# No indexable marketplace responder or hotline detail pages are configured.\n";
  }

  return `${INDEXABLE_MARKETPLACE_DETAIL_ROUTES.map(
    (entry) => `location = ${entry.path} {\n    rewrite ^ /index.html break;\n}`
  ).join("\n\n")}\n`;
}
